import { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import type { DependencyGraphTypes } from '@backstage/core-components';
import type { EntityEdgeData } from '@backstage/plugin-catalog-graph';
import { getRelationVerb } from '../lib/relations';
import { findConnections } from '../lib/connections';
import { wrapText } from '../lib/c4Layout';
import { useEntityByRef } from '../lib/useEntityByRef';
import { C4ConnectionsDialog } from './C4ConnectionsDialog';

const LINE_HEIGHT = 11;
const FONT_SIZE = 10;
const CHAR_WIDTH = 5.2;
const LABEL_WIDTH = 168;
const MAX_PRIMARY_LINES = 2;
const PADDING_X = 6;
const PADDING_Y = 4;

const useStyles = makeStyles(theme => ({
  background: {
    fill: theme.palette.background.paper,
    stroke: theme.palette.divider,
  },
  text: {
    fill: theme.palette.textContrast,
  },
  detail: {
    fill: theme.palette.textSubtle,
  },
  more: {
    fill: theme.palette.link,
    cursor: 'pointer',
  },
}));

function entityLabel(
  entity: { metadata?: { title?: string; name?: string } } | undefined,
  fallback: string | undefined,
): string {
  return entity?.metadata?.title ?? entity?.metadata?.name ?? fallback ?? '';
}

export function C4Label({
  edge,
}: DependencyGraphTypes.RenderLabelProps<EntityEdgeData>) {
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  const from = useEntityByRef(edge?.from);
  const to = useEntityByRef(edge?.to);

  const connections = [
    ...findConnections(from, edge?.to),
    ...findConnections(to, edge?.from),
  ];

  const verb = getRelationVerb(edge?.relations ?? []);
  const [first] = connections;
  const primary = first?.description || verb;
  const detail = [first?.via, first?.technology]
    .filter((part): part is string => Boolean(part))
    .join(' · ');
  const extra = connections.length - 1;

  if (!primary && !detail) {
    return null;
  }

  const primaryLines = primary
    ? wrapText(primary, LABEL_WIDTH, MAX_PRIMARY_LINES)
    : [];
  const detailLines = detail ? wrapText(detail, LABEL_WIDTH, 1) : [];
  const moreLine = extra > 0 ? `+${extra} ${extra === 1 ? 'ponto' : 'pontos'}` : '';

  const lines = [...primaryLines, ...detailLines, ...(moreLine ? [moreLine] : [])];
  const longest = lines.reduce((max, line) => Math.max(max, line.length), 0);
  const boxWidth = Math.min(LABEL_WIDTH, longest * CHAR_WIDTH) + PADDING_X * 2;
  const boxHeight = lines.length * LINE_HEIGHT + PADDING_Y * 2;
  const firstBaseline = -boxHeight / 2 + PADDING_Y + FONT_SIZE;

  let index = 0;
  const nextY = () => firstBaseline + LINE_HEIGHT * index++;

  return (
    <>
      <g data-testid="c4-label-group">
        <rect
          data-testid="c4-label-background"
          x={-boxWidth / 2}
          y={-boxHeight / 2}
          width={boxWidth}
          height={boxHeight}
          rx={3}
          className={classes.background}
        />
        <text
          data-testid="c4-label"
          textAnchor="middle"
          fontSize={FONT_SIZE}
          className={classes.text}
        >
          {primaryLines.map((line, i) => (
            <tspan
              key={`primary-${i}-${line}`}
              data-testid="c4-label-primary"
              x={0}
              y={nextY()}
            >
              {line}
            </tspan>
          ))}
          {detailLines.map((line, i) => (
            <tspan
              key={`detail-${i}-${line}`}
              data-testid="c4-label-detail"
              x={0}
              y={nextY()}
              fontStyle="italic"
              className={classes.detail}
            >
              {line}
            </tspan>
          ))}
          {moreLine && (
            <tspan
              data-testid="c4-label-more"
              x={0}
              y={nextY()}
              className={classes.more}
              onClick={() => setOpen(true)}
            >
              {moreLine}
            </tspan>
          )}
        </text>
      </g>

      {open && (
        <C4ConnectionsDialog
          open={open}
          onClose={() => setOpen(false)}
          title={`${entityLabel(from, edge?.from)} → ${entityLabel(
            to,
            edge?.to,
          )}`}
          connections={connections}
        />
      )}
    </>
  );
}
